// Customer classification enums — Invoice Cadence, Customer Type, and
// Customer Sub-Type. Canonical home so the API (schema/validators/import),
// the typed clients, and the web UI all derive from one source and can never
// drift. Stored values are stable lowercase codes (mirrors `active`/`inactive`
// on status); the human-facing label lives alongside in the *_LABELS maps and
// is what the spreadsheet writes / the dropdown shows.
//
// Extending a set is a pure TypeScript edit — the DB columns are plain text
// with NO CHECK constraint; allowed codes are enforced at the application layer
// (validators derive from these tuples), so adding a value here needs no
// migration.

export const INVOICE_CADENCES = [
  'monthly',
  'quarterly',
  'half_yearly',
  'yearly',
  'one_time',
] as const;
export type InvoiceCadence = (typeof INVOICE_CADENCES)[number];
export const INVOICE_CADENCE_LABELS: Record<InvoiceCadence, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  half_yearly: 'Half-Yearly',
  yearly: 'Yearly',
  one_time: 'One-Time',
};

export const CUSTOMER_TYPES = [
  'monthly_retainer',
  'monthly_retainer_lite',
  'quarterly_retainer',
  'hourly_quarterly',
  'half_yearly_retainer',
  'one_time_retainer',
  'yearly_retainer',
  'berkeley_skydeck',
  'silicon_catalyst',
  'batchery',
  'standard_tm',
  'hybrid_monthly',
] as const;
export type CustomerType = (typeof CUSTOMER_TYPES)[number];
export const CUSTOMER_TYPE_LABELS: Record<CustomerType, string> = {
  monthly_retainer: 'Monthly Retainer',
  monthly_retainer_lite: 'Monthly Retainer-Lite',
  quarterly_retainer: 'Quarterly Retainer',
  hourly_quarterly: 'Hourly-Quarterly',
  half_yearly_retainer: 'Half-Yearly Retainer',
  one_time_retainer: 'One-Time Retainer',
  yearly_retainer: 'Yearly Retainer',
  berkeley_skydeck: 'Berkeley SkyDeck',
  silicon_catalyst: 'Silicon Catalyst',
  batchery: 'Batchery',
  standard_tm: 'Standard T&M',
  hybrid_monthly: 'Hybrid Monthly',
};

export const CUSTOMER_SUB_TYPES = [
  'accelerator_skydeck',
  'batchery',
  'startx',
  'yc',
  'ffl',
  'sicat',
  'harvard',
  'alchemist',
  'jumpstart',
  'slp',
] as const;
export type CustomerSubType = (typeof CUSTOMER_SUB_TYPES)[number];
export const CUSTOMER_SUB_TYPE_LABELS: Record<CustomerSubType, string> = {
  accelerator_skydeck: 'Accelerator – SkyDeck',
  batchery: 'Batchery',
  startx: 'StartX',
  yc: 'YC',
  ffl: 'FFL',
  sicat: 'SiCat',
  harvard: 'Harvard',
  alchemist: 'Alchemist',
  jumpstart: 'Jumpstart',
  slp: 'SLP',
};

/** `{ value, label }` option lists for dropdowns, in declaration order. */
export interface ClassificationOption<T extends string> {
  value: T;
  label: string;
}
function toOptions<T extends string>(
  values: readonly T[],
  labels: Record<T, string>,
): ClassificationOption<T>[] {
  return values.map((value) => ({ value, label: labels[value] }));
}
export const INVOICE_CADENCE_OPTIONS = toOptions(INVOICE_CADENCES, INVOICE_CADENCE_LABELS);
/**
 * The three finance buckets, per reporting requirements 4.3.
 *
 * A `Record` over `CustomerType` rather than a partial map, so the COMPILER
 * demands an entry for a thirteenth classification. `timesheet-facts.sql`
 * carries the same mapping as a CASE it cannot import, and `model.test.ts`
 * holds the two together — the label CASE beside it already had that guard and
 * this one did not, so a new code would have kept its label and silently
 * dropped out of every Client Type figure.
 *
 * `hybrid_monthly` is `null` ON PURPOSE: the finance sheet does not place it,
 * and guessing would put real revenue in the wrong bucket. `null` is "no
 * bucket", which is a different statement from "not yet mapped" — and the type
 * is what makes the difference statable.
 */
export type ClientType = 'Retainer' | 'Hourly' | '$1+Free';

export const CLIENT_TYPE_BY_CUSTOMER_TYPE: Record<CustomerType, ClientType | null> = {
  monthly_retainer: 'Retainer',
  monthly_retainer_lite: 'Retainer',
  quarterly_retainer: 'Retainer',
  half_yearly_retainer: 'Retainer',
  yearly_retainer: 'Retainer',
  one_time_retainer: 'Retainer',
  standard_tm: 'Hourly',
  hourly_quarterly: 'Hourly',
  berkeley_skydeck: '$1+Free',
  silicon_catalyst: '$1+Free',
  batchery: '$1+Free',
  hybrid_monthly: null,
};

export const CUSTOMER_TYPE_OPTIONS = toOptions(CUSTOMER_TYPES, CUSTOMER_TYPE_LABELS);
export const CUSTOMER_SUB_TYPE_OPTIONS = toOptions(CUSTOMER_SUB_TYPES, CUSTOMER_SUB_TYPE_LABELS);

/** Strip a string to a comparison token: lowercase, alphanumeric only. So
 *  "Half-Yearly", "half_yearly", and "  HALF YEARLY " all collapse to the
 *  same key — import accepts either the code or the label, punctuation- and
 *  case-insensitively (matters for "Accelerator – SkyDeck"'s en dash). Shared
 *  with the customers importer's header matching so the two never diverge. */
export function normalizeToken(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** Build a tolerant cell→code resolver for one enum: accepts the stored code
 *  or the human label, ignoring case/punctuation. Returns undefined for an
 *  unrecognized cell so the importer can emit a clear per-row error. */
export function buildClassificationMatcher<T extends string>(
  values: readonly T[],
  labels: Record<T, string>,
): (cell: string) => T | undefined {
  const byToken = new Map<string, T>();
  for (const value of values) {
    byToken.set(normalizeToken(value), value);
    byToken.set(normalizeToken(labels[value]), value);
  }
  return (cell: string) => byToken.get(normalizeToken(cell));
}

/**
 * Which `customer_type` values the month-end retainer run bills.
 *
 * Everything except `standard_tm`: a time-and-materials customer is billed
 * for hours actually worked, so there is nothing to raise in advance. The
 * accelerator programmes (SkyDeck, Silicon Catalyst, Batchery) and the
 * hourly-quarterly and hybrid types all carry a recurring fee alongside
 * whatever else they do, so the run raises that part and a human adds the
 * rest.
 *
 * Deriving it by exclusion rather than listing the retainer types is
 * deliberate: a NEW customer type added to the tuple above is far more likely
 * to be a retainer variant than another pure-T&M one, and the failure mode of
 * getting it wrong that way round — an invoice raised that needed deleting —
 * is much cheaper than the other — a customer silently never billed.
 *
 * BUSINESS CALL WORTH CONFIRMING: `hourly_quarterly` is included on the
 * reading that the "quarterly" half is a recurring fee. If it is purely
 * hourly, remove it here and it stops being picked up; nothing else changes.
 */
const NON_RETAINER_CUSTOMER_TYPES: ReadonlySet<CustomerType> = new Set(['standard_tm']);

export function isRetainerCustomerType(v: unknown): v is CustomerType {
  return (
    typeof v === 'string' &&
    (CUSTOMER_TYPES as readonly string[]).includes(v) &&
    !NON_RETAINER_CUSTOMER_TYPES.has(v as CustomerType)
  );
}

/**
 * True when invoice generation can raise an invoice for this customer type.
 *
 * Today this is exactly {@link isRetainerCustomerType}, and it is still worth
 * its own name because it answers a DIFFERENT question. "Is this a retainer
 * engagement?" is about the commercial shape of the relationship. "Can we
 * generate for it?" is about what this codebase has built — and the two
 * coincide only because the sole pricing path reads SOW retainer lines.
 *
 * The day timesheet-driven billing ships, `standard_tm` becomes generatable
 * while remaining, correctly, not a retainer. Wiring the picker to
 * `isRetainerCustomerType` would then grey out the one type the new code
 * exists to serve, and the bug would look like a UI oversight rather than a
 * conflated concept.
 *
 * The generation service and the type picker must both read THIS, so the
 * greyed-out options and the engine's eligibility gate can never disagree
 * about what is supported.
 */
export function isGeneratableCustomerType(v: unknown): v is CustomerType {
  return isRetainerCustomerType(v);
}

/**
 * Plain membership guards for the two classification vocabularies.
 *
 * Distinct from {@link isRetainerCustomerType}, which answers a business
 * question on top of membership. These answer only "is this one of the values
 * we know", which is what a reader narrowing a snapshotted text column needs:
 * a value written before the current vocabulary should render as unknown
 * rather than be cast into a type it does not belong to.
 */
export function isCustomerType(v: unknown): v is CustomerType {
  return typeof v === 'string' && (CUSTOMER_TYPES as readonly string[]).includes(v);
}

export function isInvoiceCadence(v: unknown): v is InvoiceCadence {
  return typeof v === 'string' && (INVOICE_CADENCES as readonly string[]).includes(v);
}

/**
 * How a late payment fee is expressed. Mirrors `customers.late_fee_kind`.
 *
 * There is deliberately no `enabled` flag beside it: a fee with no amount has
 * nothing to charge, so "enabled" is derived from the amount being present.
 * Two fields that can disagree about the same fact is one field too many.
 */
export const LATE_FEE_KINDS = ['percentage', 'fixed_amount'] as const;
export type LateFeeKind = (typeof LATE_FEE_KINDS)[number];

export const LATE_FEE_KIND_LABELS: Record<LateFeeKind, string> = {
  percentage: 'Percentage',
  fixed_amount: 'Fixed amount',
};

export function isLateFeeKind(v: unknown): v is LateFeeKind {
  return typeof v === 'string' && (LATE_FEE_KINDS as readonly string[]).includes(v);
}

/** True when a late payment fee is configured — the derived "enabled". */
export function hasLateFee(c: {
  lateFeeKind?: LateFeeKind | string | null;
  lateFeeAmount?: number | string | null;
}): boolean {
  return c.lateFeeKind != null && c.lateFeeAmount != null;
}
